import type { DrawGuessStroke } from "@topzle/game-draw-guess";

export interface DrawGuessPrediction {
  label: string;
  confidence: number;
}

interface ClassifyRequest {
  id: number;
  type: "classify";
  bitmaps: ArrayBuffer[];
  limit: number;
}

interface WarmRequest {
  id: number;
  type: "warm";
}

type WorkerRequest = ClassifyRequest | WarmRequest;

interface WorkerSuccess {
  id: number;
  ok: true;
  predictions: DrawGuessPrediction[];
}

interface WorkerFailure {
  id: number;
  ok: false;
  error: string;
}

type WorkerResponse = WorkerSuccess | WorkerFailure;

interface PendingRequest {
  resolve: (predictions: DrawGuessPrediction[]) => void;
  reject: (error: Error) => void;
}

let classifierWorker: Worker | null = null;
let nextRequestId = 1;
const pendingRequests = new Map<number, PendingRequest>();

function rejectPendingRequests(message: string) {
  for (const request of pendingRequests.values()) request.reject(new Error(message));
  pendingRequests.clear();
}

function getClassifierWorker(): Worker {
  if (typeof Worker === "undefined")
    throw new Error("Local drawing recognition requires a browser");
  if (classifierWorker) return classifierWorker;

  const worker = new Worker("/models/draw-guess/litert/draw-guess-worker.js", {
    name: "topzle-draw-guess-ai",
  });
  worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
    const response = event.data;
    const pending = pendingRequests.get(response.id);
    if (!pending) return;
    pendingRequests.delete(response.id);
    if (response.ok) pending.resolve(response.predictions);
    else pending.reject(new Error(response.error));
  });
  worker.addEventListener("error", (event) => {
    rejectPendingRequests(
      event.message
        ? `The local drawing AI stopped unexpectedly: ${event.message}`
        : "The local drawing AI stopped unexpectedly",
    );
    worker.terminate();
    if (classifierWorker === worker) classifierWorker = null;
  });
  classifierWorker = worker;
  return worker;
}

function runWorkerRequest(
  request: Omit<ClassifyRequest, "id"> | Omit<WarmRequest, "id">,
): Promise<DrawGuessPrediction[]> {
  const worker = getClassifierWorker();
  const id = nextRequestId++;
  return new Promise((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject });
    const message = { ...request, id } as WorkerRequest;
    if (message.type === "classify") worker.postMessage(message, message.bitmaps);
    else worker.postMessage(message);
  });
}

export async function warmDrawGuessClassifier(): Promise<void> {
  await runWorkerRequest({ type: "warm" });
}

export function resampleDrawGuessPoints(
  points: [number, number][],
  limit = 64,
): [number, number][] {
  if (points.length <= limit) return points;
  if (limit < 2) return points.slice(0, Math.max(0, limit));

  const cumulativeDistances = [0];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const point = points[index];
    cumulativeDistances.push(
      (cumulativeDistances[index - 1] ?? 0) +
        (previous && point ? Math.hypot(point[0] - previous[0], point[1] - previous[1]) : 0),
    );
  }
  const totalDistance = cumulativeDistances.at(-1) ?? 0;
  if (totalDistance === 0) return [points[0] ?? [0, 0]];

  const sampled: [number, number][] = [];
  let segmentIndex = 1;
  for (let sampleIndex = 0; sampleIndex < limit; sampleIndex += 1) {
    const targetDistance = (totalDistance * sampleIndex) / (limit - 1);
    while (
      segmentIndex < cumulativeDistances.length - 1 &&
      (cumulativeDistances[segmentIndex] ?? 0) < targetDistance
    )
      segmentIndex += 1;
    const previous = points[segmentIndex - 1] ?? points[0] ?? [0, 0];
    const point = points[segmentIndex] ?? points.at(-1) ?? previous;
    const startDistance = cumulativeDistances[segmentIndex - 1] ?? 0;
    const endDistance = cumulativeDistances[segmentIndex] ?? startDistance;
    const progress =
      endDistance === startDistance
        ? 0
        : (targetDistance - startDistance) / (endDistance - startDistance);
    sampled.push([
      Math.round(previous[0] + (point[0] - previous[0]) * progress),
      Math.round(previous[1] + (point[1] - previous[1]) * progress),
    ]);
  }
  return sampled;
}

function paintPixel(bitmap: Uint8Array, x: number, y: number, radius: number, erase: boolean) {
  const antialiasRadius = radius + 0.75;
  for (let offsetY = -Math.ceil(antialiasRadius); offsetY <= antialiasRadius; offsetY += 1) {
    for (let offsetX = -Math.ceil(antialiasRadius); offsetX <= antialiasRadius; offsetX += 1) {
      const distance = Math.hypot(offsetX, offsetY);
      if (distance > antialiasRadius) continue;
      const pixelX = x + offsetX;
      const pixelY = y + offsetY;
      if (pixelX < 0 || pixelX >= 28 || pixelY < 0 || pixelY >= 28) continue;
      const index = pixelY * 28 + pixelX;
      const intensity = Math.round(255 * Math.min(1, antialiasRadius - distance));
      bitmap[index] = erase ? 0 : Math.max(bitmap[index] ?? 0, intensity);
    }
  }
}

function rasterizeWithTransform(
  strokes: DrawGuessStroke[],
  mapPoint: (point: [number, number]) => readonly [number, number],
): Uint8Array {
  const bitmap = new Uint8Array(28 * 28);
  for (const stroke of strokes) {
    // Recognition is deliberately color-independent: black and every other
    // brush color become the same dark line expected by the model.
    const radius = Math.max(1, Math.min(4, stroke.size / 12));
    const erase = stroke.mode === "erase";
    const points = stroke.points.map(mapPoint);
    for (let pointIndex = 0; pointIndex < points.length; pointIndex += 1) {
      const point = points[pointIndex];
      if (!point) continue;
      const previous = points[Math.max(0, pointIndex - 1)] ?? point;
      const distance = Math.max(Math.abs(point[0] - previous[0]), Math.abs(point[1] - previous[1]));
      for (let step = 0; step <= Math.max(1, Math.ceil(distance * 2)); step += 1) {
        const progress = distance === 0 ? 1 : step / Math.ceil(distance * 2);
        const x = Math.round(previous[0] + (point[0] - previous[0]) * progress);
        const y = Math.round(previous[1] + (point[1] - previous[1]) * progress);
        paintPixel(bitmap, x, y, radius, erase);
      }
    }
  }
  return bitmap;
}

function rasterizeCroppedStrokes(strokes: DrawGuessStroke[], span: number): Uint8Array {
  // Eraser gestures must not change the drawing's scale or position.
  const allPoints = strokes
    .filter((stroke) => stroke.mode !== "erase")
    .flatMap((stroke) => stroke.points);
  if (allPoints.length === 0) return new Uint8Array(28 * 28);
  const xs = allPoints.map(([x]) => x);
  const ys = allPoints.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const scale = span / Math.max(width, height);
  const margin = (28 - span) / 2;
  const offsetX = margin + (span - width * scale) / 2;
  const offsetY = margin + (span - height * scale) / 2;
  const mapPoint = ([x, y]: [number, number]) =>
    [offsetX + (x - minX) * scale, offsetY + (y - minY) * scale] as const;
  return rasterizeWithTransform(strokes, mapPoint);
}

export function rasterizeDrawGuessStrokes(strokes: DrawGuessStroke[]): Uint8Array {
  // This matches the model author's recommended preprocessing: resize the
  // complete drawing canvas to 28×28 instead of always zooming into its bounds.
  return rasterizeWithTransform(strokes, ([x, y]) => [
    (Math.max(0, Math.min(1000, x)) / 1000) * 27,
    (Math.max(0, Math.min(1000, y)) / 1000) * 27,
  ]);
}

export function rasterizeDrawGuessStrokeViews(strokes: DrawGuessStroke[]): Uint8Array[] {
  // People draw at very different sizes. The full-canvas view follows model
  // training, while two bounded views make small line drawings recognizable.
  return [
    rasterizeDrawGuessStrokes(strokes),
    rasterizeCroppedStrokes(strokes, 20),
    rasterizeCroppedStrokes(strokes, 24),
  ];
}

export function prepareDrawGuessModelInput(bitmap: Uint8Array): Float32Array {
  if (bitmap.length !== 28 * 28) throw new Error("Drawing bitmap has the wrong dimensions");
  const input = new Float32Array(bitmap.length);
  // Quick Draw bitmap weights expect empty pixels at 0 and foreground ink at 1.
  // The visible canvas may use black ink on white; rasterization has already
  // converted every brush color into this color-independent foreground mask.
  for (let index = 0; index < bitmap.length; index += 1) input[index] = (bitmap[index] ?? 0) / 255;
  return input;
}

export function countDrawGuessInk(bitmap: Uint8Array): number {
  let count = 0;
  for (const value of bitmap) {
    if (value > 24) count += 1;
  }
  return count;
}

export async function classifyDrawGuessStrokes(
  strokes: DrawGuessStroke[],
  limit = 5,
): Promise<DrawGuessPrediction[]> {
  const bitmaps = rasterizeDrawGuessStrokeViews(strokes);
  if (Math.max(...bitmaps.map(countDrawGuessInk)) < 10) return [];
  const transferableBitmaps = bitmaps.map(
    (bitmap) =>
      bitmap.buffer.slice(bitmap.byteOffset, bitmap.byteOffset + bitmap.byteLength) as ArrayBuffer,
  );
  return runWorkerRequest({
    type: "classify",
    bitmaps: transferableBitmaps,
    limit: Math.max(1, Math.min(10, Math.round(limit))),
  });
}
